import { loadStripe, Stripe } from "@stripe/stripe-js";
// ⚠️ Base64系は使わない（テキストを載せないため）
// import { toBase64UrlUtf8 } from "../utils/base64"; // ← もう不要

// 1) 公開キーは .env から読み込む（必ずASCII）
// 環境変数から取得
const envKey = (import.meta as any).env?.VITE_STRIPE_PUBLISHABLE_KEY;

// プレースホルダーかどうかをチェック
function isPlaceholderKey(key: string | undefined): boolean {
  if (!key) return true;
  const trimmed = key.trim();
  return (
    trimmed.includes('your_') || 
    trimmed.includes('YOUR_') || 
    trimmed === 'your_stripe_publishable_key' ||
    trimmed.length < 20 || // 短すぎる場合はプレースホルダーと判断
    !trimmed.startsWith('pk_') // Stripe公開キーの形式でない場合
  );
}

// 本番環境のStripe公開キー（フォールバック）
const FALLBACK_KEY = "pk_live_51SAVwbLVferAdY9v3YaRfLpfropM9r2aWl2DvPBaHxvlerM0SD3AVNU9cAQrrESJMFfqL8k95x2DOydMdtLRsLJ600SEokyUTM";

// 環境変数が有効な場合はそれを使用、そうでなければフォールバック
let STRIPE_PUBLISHABLE_KEY: string;
if (envKey && !isPlaceholderKey(envKey)) {
  STRIPE_PUBLISHABLE_KEY = envKey.trim();
  console.log('✅ 環境変数からStripe公開キーを読み込みました');
} else {
  STRIPE_PUBLISHABLE_KEY = FALLBACK_KEY;
  console.log('⚠️ 環境変数がプレースホルダーまたは未設定のため、フォールバックキーを使用します');
}

console.log('🔍 Stripe公開キー設定:', {
  hasEnvKey: !!envKey,
  envKeyValue: envKey ? envKey.substring(0, 20) + '...' : 'none',
  isPlaceholder: isPlaceholderKey(envKey),
  usingFallback: !envKey || isPlaceholderKey(envKey),
  finalKeyPreview: STRIPE_PUBLISHABLE_KEY.substring(0, 20) + '...'
});

// Stripe公開キーの完全検証（書式・ASCII・誤コピーチェック）
function validateStripePublishableKey(key: string): string {
  const PK = key.trim();
  
  console.log('🔍 Stripe公開キー検証開始:', {
    originalLength: key.length,
    trimmedLength: PK.length,
    firstChars: PK.substring(0, 10),
    lastChars: PK.substring(PK.length - 10)
  });
  
  // プレースホルダーチェック（検証前に実行）
  if (PK.includes('your_') || PK.includes('YOUR_') || PK === 'your_stripe_publishable_key' || PK.length < 20) {
    console.error('❌ Stripe公開キーがプレースホルダーです:', PK);
    throw new Error("Stripe公開キーがプレースホルダーのままです。環境変数VITE_STRIPE_PUBLISHABLE_KEYを設定するか、.env.localファイルを確認してください。");
  }
  
  // 書式チェック：pk_test_またはpk_live_で始まり、英数字のみ
  if (!/^pk_(test|live)_[A-Za-z0-9]+$/.test(PK)) {
    console.error('❌ Stripe公開キーの書式が不正です:', PK);
    throw new Error("Stripe公開キーの書式が不正です（余計な文字/全角/改行混入の可能性）");
  }
  
  // ASCII文字チェック：一文字ずつ確認
  for (const ch of PK) {
    if (ch.charCodeAt(0) > 0x7f) {
      console.error('❌ 非ASCII文字検出:', {
        char: ch,
        charCode: ch.charCodeAt(0),
        position: PK.indexOf(ch)
      });
      throw new Error("Stripe公開キーに非ASCII文字が含まれています（全角混入）");
    }
  }
  
  // 長さチェック：通常のStripe公開キーの長さ
  if (PK.length < 100 || PK.length > 150) {
    console.error('❌ Stripe公開キーの長さが異常です:', PK.length);
    throw new Error("Stripe公開キーの長さが異常です（誤コピーの可能性）");
  }
  
  console.log('✅ Stripe公開キー検証完了:', {
    format: 'valid',
    ascii: 'valid',
    length: PK.length,
    type: PK.startsWith('pk_test_') ? 'test' : 'live',
    keyPreview: PK.substring(0, 20) + '...' + PK.substring(PK.length - 10)
  });

  return PK;
}

// 公開キーを検証して使用
const stripePublishableKey = validateStripePublishableKey(STRIPE_PUBLISHABLE_KEY);

let stripePromise: Promise<Stripe | null>;
export const getStripe = (): Promise<Stripe | null> => {
  if (!stripePromise) stripePromise = loadStripe(stripePublishableKey);
  return stripePromise;
};

// Stripe Checkout（Functions の httpsCallable 版）
export const redirectToCheckout = async (
  plan: "standard" | "pro" | "pro_max",
  userId: string,
  userEmail: string
): Promise<void> => {
  try {
    const stripe = await getStripe();
    if (!stripe) throw new Error("Stripeが初期化されていません");

    console.log("Stripe決済開始:", { plan, userId, userEmail });

    // プラン名からPrice IDを取得（本番環境）
    const priceIds = {
      standard: "price_1SFtDxLVferAdY9vYX7AxEht",
      pro: "price_1SFtEZLVferAdY9vW3LArvGA",
      pro_max: "price_1SFtFSLVferAdY9vmdAekB4M"
    };

    const priceId = priceIds[plan];
    if (!priceId) {
      throw new Error(`不明なプランです: ${plan}`);
    }

    // URLを生成
    const baseUrl = window.location.origin;
    const successUrl = `${baseUrl}/payment-success?session_id={CHECKOUT_SESSION_ID}`;
    const cancelUrl = `${baseUrl}/payment-cancel`;

    const { getFunctions, httpsCallable, connectFunctionsEmulator } = await import("firebase/functions");
    const { getApp } = await import("firebase/app");
    
    const firebaseApp = getApp();
    const functions = getFunctions(firebaseApp, 'us-central1');
    
    // 環境変数でエミュレーター接続を制御
    const useEmulator = import.meta.env.VITE_USE_FUNCTIONS_EMULATOR === 'true';
    const isLocalhost = typeof window !== 'undefined' && 
                       (window.location.hostname === 'localhost' || 
                        window.location.hostname === '127.0.0.1');
    
    if (useEmulator && isLocalhost) {
      try {
        connectFunctionsEmulator(functions, 'localhost', 5001);
      } catch (error: any) {
        if (!error.message?.includes('already been called') && 
            !error.message?.includes('already connected')) {
          console.warn('⚠️ Functions Emulator接続エラー（本番環境に接続します）');
        }
      }
    }
    
    const callable = httpsCallable(functions, "createCheckoutSession");

    // 必要なパラメータを渡す
    const result = await callable({ 
      priceId, 
      successUrl, 
      cancelUrl 
    });
    
    const { sessionId, checkoutUrl } = (result.data ?? {}) as {
      sessionId?: string;
      checkoutUrl?: string;
    };

    if (checkoutUrl) {
      console.log("✅ サーバー生成URLへ遷移:", checkoutUrl);
      window.location.assign(checkoutUrl);
      return;
    }
    if (sessionId) {
      // フォールバック：Stripeの公式リダイレクト
      const { error } = await stripe.redirectToCheckout({ sessionId });
      if (error) throw new Error(error.message || "決済ページへのリダイレクトに失敗しました");
      return;
    }

    throw new Error("サーバーからcheckoutUrlもsessionIdも受け取れませんでした。");

  } catch (err: any) {
    console.error("決済リダイレクトエラー:", err);

    // Functionsのエラーメッセージはそのまま（UTF-8）で扱う。Base64化しない。
    const msg = String(err?.message || "決済の開始に失敗しました。");
    // 簡易マッピング（必要に応じて拡張）
    const map: Record<string, string> = {
      "Authentication required": "ログインが必要です",
      "Invalid plan": "不明なプランです",
    };
    throw new Error(map[msg] ?? msg);
  }
};

// REST 経由のセッション作成（必要に応じて使用）
export async function createCheckoutSession(payload: {
  userId: string;
  planId: string;
  note?: string | null; // ← 人間向け文言はサーバーに保存 or UI表示で、URLに載せない
}) {
  const res = await fetch("/api/createCheckout", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  const raw = await res.text();
  let json: any = null;
  try { json = raw ? JSON.parse(raw) : null; } catch {}

  if (!res.ok || json?.error) {
    const msg = json?.error?.message || raw || "決済の開始に失敗しました。";
    throw new Error(msg); // ← Base64にしない
  }

  if (json?.checkoutUrl) {
    return { checkoutUrl: json.checkoutUrl, sessionId: json.sessionId ?? null };
  }
  if (json?.sessionId) {
    // セッションIDのみ返ってきた場合のフォールバック
    const qs = new URLSearchParams({ sid: json.sessionId as string }).toString();
    return { checkoutUrl: `/checkout?${qs}`, sessionId: json.sessionId };
  }

  throw new Error("サーバー応答にcheckoutUrl / sessionIdが含まれていません。");
}

// 決済成功後の処理
export const handlePaymentSuccess = async (sessionId: string): Promise<void> => {
  const response = await fetch("/api/payment-success", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ sessionId }),
  });
  if (!response.ok) throw new Error("決済成功処理に失敗しました");
  console.log("決済が正常に完了しました");
};

// Stripe Customer Portal（サブスクリプション管理）へリダイレクト
export const redirectToCustomerPortal = async (): Promise<void> => {
  try {
    console.log("Customer Portal リダイレクト開始");

    const baseUrl = window.location.origin;
    const returnUrl = `${baseUrl}/`; // LPに戻る

    const { getFunctions, httpsCallable, connectFunctionsEmulator } = await import("firebase/functions");
    const { getApp } = await import("firebase/app");
    
    const firebaseApp = getApp();
    const functions = getFunctions(firebaseApp, 'us-central1');
    
    // 環境変数でエミュレーター接続を制御
    const useEmulator = import.meta.env.VITE_USE_FUNCTIONS_EMULATOR === 'true';
    const isLocalhost = typeof window !== 'undefined' && 
                       (window.location.hostname === 'localhost' || 
                        window.location.hostname === '127.0.0.1');
    
    if (useEmulator && isLocalhost) {
      try {
        connectFunctionsEmulator(functions, 'localhost', 5001);
      } catch (error: any) {
        if (!error.message?.includes('already been called') && 
            !error.message?.includes('already connected')) {
          console.warn('⚠️ Functions Emulator接続エラー（本番環境に接続します）');
        }
      }
    }
    
    const callable = httpsCallable(functions, "createPortalSession");

    const result = await callable({ returnUrl });
    
    const { url } = (result.data ?? {}) as { url?: string };

    if (url) {
      console.log("✅ Customer Portal URLへ遷移:", url);
      window.location.assign(url);
      return;
    }

    throw new Error("ポータルURLを取得できませんでした。");

  } catch (err: any) {
    console.error("Customer Portal リダイレクトエラー:", err);
    throw new Error(err?.message || "サブスクリプション管理ページへの遷移に失敗しました。");
  }
};
